import { ACTION_STATUSES, PAYMENT_TYPE } from 'shared/consts';
import { setCurrentPaymentMethodId } from 'store/payment/actions';
import {
  selectCurrentPaymentMethodId,
  selectGetPaymentMethodsStatus,
  selectPaymentMethods,
  selectSavePaymentMethodStatus,
} from 'store/payment/selectors';
import { useDispatch, useSelector } from 'react-redux';

import { SavedPaymentMethod } from 'store/payment/types';
import { findCurrentPaymentMethod } from 'utils/findCurrentPaymentMethod';
import { useEffect } from 'react';
import { APPLE_PAY } from 'shared/consts/payment-method-types';

export interface UsePaymentMethodsInterface {
  isPaymentMethodsLoading: boolean;
  paymentMethods: Array<SavedPaymentMethod>;
  isPaymentMethodsFullfiled: boolean;
  currentPaymentMethodId: string;
}

export const usePaymentMethods = (): UsePaymentMethodsInterface => {
  const currentPaymentMethodId = useSelector(selectCurrentPaymentMethodId);
  const paymentMethodsStatus = useSelector(selectGetPaymentMethodsStatus);
  const paymentMethods = useSelector(selectPaymentMethods);
  const savePaymentMethodStatus = useSelector(selectSavePaymentMethodStatus);
  const dispatch = useDispatch();

  useEffect(() => {
    const isExternalPaymentMethod =
      currentPaymentMethodId === PAYMENT_TYPE.GOOGLE_PAY || currentPaymentMethodId === PAYMENT_TYPE.EXTERNAL;

    // After a card is saved, its id becomes the current selection (via savePaymentMethod
    // success) before the payment-methods list is refetched. During that window the id is
    // not yet in `paymentMethods`, so we must not fall back to the last method here —
    // otherwise the freshly added card is dropped. Once the refetch lands the id is found
    // and this guard is irrelevant.
    const isSavedCardPendingRefetch = savePaymentMethodStatus === ACTION_STATUSES.FULFILLED;

    if (
      paymentMethods &&
      paymentMethods.length > 0 &&
      !isSavedCardPendingRefetch &&
      (!currentPaymentMethodId ||
        (!findCurrentPaymentMethod(paymentMethods, currentPaymentMethodId) && !isExternalPaymentMethod))
    ) {
      dispatch(setCurrentPaymentMethodId(paymentMethods[paymentMethods.length - 1]?.id?.toString()));
    }
  }, [paymentMethods, currentPaymentMethodId, savePaymentMethodStatus, dispatch]);

  useEffect(() => {
    if (paymentMethods?.some(method => method.payment_method_type === APPLE_PAY)) {
      dispatch(setCurrentPaymentMethodId('-1'));
    }
  }, []);

  const isPaymentMethodsLoading = paymentMethodsStatus === ACTION_STATUSES.PENDING;
  const isPaymentMethodsFullfiled = paymentMethodsStatus === ACTION_STATUSES.FULFILLED;

  return {
    isPaymentMethodsLoading,
    paymentMethods,
    isPaymentMethodsFullfiled,
    currentPaymentMethodId,
  };
};
